import { legalContact } from "../legalContact";

const termsOfService = {
  eyebrow: "Legal",
  title: "Terms of Service",
  summary: "The terms that apply when you access or use the AV Facade Services Ltd website.",
  effectiveDate: "1 July 2026",
  // The source document carries no "Last updated" line, so no chip is rendered for it.
  intro: [
    {
      type: "paragraph",
      text: "Welcome to the AV Facade Services Ltd website. By accessing or using this website, you agree to comply with and be bound by these Terms of Service. If you do not agree with these terms, please do not use this website.",
    },
  ],
  sections: [
    {
      id: "about-us",
      title: "About Us",
      blocks: [
        { type: "paragraph", text: "This website is owned and operated by:" },
        { type: "address" },
        {
          type: "paragraph",
          text: 'Throughout these Terms, "we", "our", and "us" refer to AV Facade Services Ltd.',
        },
      ],
    },
    {
      id: "website-use",
      title: "Website Use",
      blocks: [
        {
          type: "paragraph",
          text: "You agree to use this website only for lawful purposes.",
        },
        { type: "paragraph", text: "You must not:" },
        {
          type: "list",
          items: [
            "Use the website in any way that breaches applicable laws or regulations.",
            "Attempt to gain unauthorised access to our website, servers or systems.",
            "Introduce viruses, malware or other harmful code.",
            "Copy, reproduce or exploit website content without our written permission.",
            "Use the website in any manner that could damage or disrupt its operation.",
          ],
        },
      ],
    },
    {
      id: "information-on-this-website",
      title: "Information on This Website",
      blocks: [
        {
          type: "paragraph",
          text: "The information provided on this website is for general guidance only.",
        },
        {
          type: "paragraph",
          text: "While we make reasonable efforts to ensure all information is accurate and up to date, we do not guarantee that all content is complete, accurate or current.",
        },
        {
          type: "paragraph",
          text: "Website content may be updated or changed without prior notice.",
        },
      ],
    },
    {
      id: "quotations-and-services",
      title: "Quotations and Services",
      blocks: [
        {
          type: "paragraph",
          text: "Information provided on this website does not constitute a contractual offer.",
        },
        {
          type: "paragraph",
          text: "Any quotation or proposal issued by AV Facade Services Ltd is subject to separate commercial terms and conditions agreed between the parties.",
        },
        {
          type: "paragraph",
          text: "Submission of an enquiry through this website does not create a contractual relationship.",
        },
      ],
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property",
      blocks: [
        {
          type: "paragraph",
          text: "Unless otherwise stated, all website content is owned by or licensed to AV Facade Services Ltd, including:",
        },
        {
          type: "list",
          items: [
            "Logos",
            "Branding",
            "Graphics",
            "Photographs",
            "Text",
            "Documents",
            "Designs",
            "Layouts",
          ],
        },
        {
          type: "paragraph",
          text: "You may not reproduce, distribute, modify or republish any content without our prior written consent.",
        },
      ],
    },
    {
      id: "third-party-links",
      title: "Third-Party Links",
      blocks: [
        {
          type: "paragraph",
          text: "This website may contain links to third-party websites.",
        },
        { type: "paragraph", text: "These links are provided for convenience only." },
        {
          type: "paragraph",
          text: "We are not responsible for the content, availability or privacy practices of external websites.",
        },
      ],
    },
    {
      id: "limitation-of-liability",
      title: "Limitation of Liability",
      blocks: [
        {
          type: "paragraph",
          text: "To the fullest extent permitted by law, AV Facade Services Ltd shall not be liable for any direct, indirect, incidental or consequential loss or damage arising from:",
        },
        {
          type: "list",
          items: [
            "Use of this website",
            "Inability to access the website",
            "Reliance on information published on the website",
            "Technical interruptions or website downtime",
            "Viruses or other harmful software transmitted through the website",
          ],
        },
        {
          type: "paragraph",
          text: "Nothing in these Terms excludes liability that cannot legally be excluded under applicable law.",
        },
      ],
    },
    {
      id: "availability",
      title: "Availability",
      blocks: [
        { type: "paragraph", text: "We aim to keep our website available at all times." },
        {
          type: "paragraph",
          text: "However, we do not guarantee uninterrupted access and reserve the right to suspend, modify or withdraw the website without notice.",
        },
      ],
    },
    {
      id: "privacy",
      title: "Privacy",
      blocks: [
        {
          type: "paragraph",
          text: [
            "Your use of this website is also governed by our ",
            { text: "Privacy Policy", to: "/privacypolicy" },
            ", which explains how we collect and process personal information.",
          ],
        },
      ],
    },
    {
      id: "cookies",
      title: "Cookies",
      blocks: [
        {
          type: "paragraph",
          text: "Our website may use cookies to improve functionality and user experience.",
        },
        {
          type: "paragraph",
          text: [
            "Further information can be found in our ",
            { text: "Cookie Policy", to: "/cookiepolicy" },
            ".",
          ],
        },
      ],
    },
    {
      id: "user-enquiries",
      title: "User Enquiries",
      blocks: [
        {
          type: "paragraph",
          text: "Where you submit information through our contact form, you confirm that:",
        },
        {
          type: "list",
          items: [
            "The information you provide is accurate.",
            "You have authority to provide the information.",
            "You are not submitting unlawful or offensive material.",
          ],
        },
        {
          type: "paragraph",
          text: "We reserve the right to ignore or remove inappropriate submissions.",
        },
      ],
    },
    {
      id: "changes-to-these-terms",
      title: "Changes to These Terms",
      blocks: [
        {
          type: "paragraph",
          text: "We may amend these Terms of Service at any time.",
        },
        {
          type: "paragraph",
          text: "Any updates will be published on this page with a revised effective date.",
        },
        {
          type: "paragraph",
          text: "Continued use of the website after changes are published constitutes acceptance of the updated Terms.",
        },
      ],
    },
    {
      id: "governing-law",
      title: "Governing Law",
      blocks: [
        {
          type: "paragraph",
          text: "These Terms of Service are governed by the laws of England and Wales.",
        },
        {
          type: "paragraph",
          text: "Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
        },
      ],
    },
    {
      id: "contact-information",
      title: "Contact Information",
      blocks: [
        {
          type: "paragraph",
          text: "If you have any questions regarding these Terms of Service, please contact:",
        },
        {
          type: "address",
          contact: { ...legalContact, lines: legalContact.lines.slice(0, 3) },
        },
      ],
    },
  ],
};

export default termsOfService;
